import { useQueryClient } from "@tanstack/react-query";
import { Check, CircleCheck, Eye, Pencil, TriangleAlert } from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";
import { useOutletContext } from "react-router";
import {
  ACCENTS,
  type Appearance,
  type AppearanceDto,
  contrastText,
  logoRadius,
  THEME_DESC,
  THEME_IDS,
  THEMES,
  themeDefaults,
  withTheme,
} from "../../../shared/appearance";
import {
  appearanceApi,
  keys,
  openPreview,
  useAppearance,
  useDraft,
  useMenus,
  useProfile,
} from "../../api/queries";
import { Bar, Modal } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import { BRAND_ACCEPT, type BrandImageKind, brandFileError, prepareBrandImage } from "../../lib/brandImage";
import { initialsOf, plural, price } from "../../lib/format";
import type { ShellContext } from "../AppShell";
import { mainMenu } from "../menus/status";
import "./appearance.css";

// "Aspect și personalizare" (prototype page `appearance`, `apVals`): theme, accent, logo and cover. Every choice
// is saved to the appearance draft at once (the prototype has no separate save); customers see it only after
// "Publică aspectul". The live preview uses the restaurant's own name and draft menu, never demo content.

const mediaUrl = (rid: string, id: string) => `/api/restaurants/${rid}/media/${id}`;

export function AppearancePage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const canEdit = membership.role !== "staff";
  const q = useAppearance(rid);
  const qc = useQueryClient();
  const toast = useToast();
  const ap = appearanceApi(rid);
  const [dialog, setDialog] = useState<"publish" | "discard" | null>(null);
  const [busy, setBusy] = useState<BrandImageKind | "save" | null>(null);
  const [imgErr, setImgErr] = useState<Record<BrandImageKind, string>>({ logo: "", cover: "" });
  const logoInput = useRef<HTMLInputElement>(null);
  const coverInput = useRef<HTMLInputElement>(null);
  const menus = useMenus(rid);
  const main = mainMenu(menus.data ?? []);

  const header = (
    <div className="stack" style={{ gap: 6 }}>
      <h1 className="h1">Aspect și personalizare</h1>
      <p className="page-sub ap-lead">
        Aspectul meniului public: tema, accentul, logo-ul și coperta. Modificările rămân în ciornă până
        publici aspectul.
      </p>
    </div>
  );
  if (q.isError) {
    return (
      <>
        {header}
        <Alert tone="error" live>
          {q.error.message}
        </Alert>
      </>
    );
  }
  if (!q.data) {
    return (
      <>
        {header}
        <p className="muted" role="status">
          Se încarcă aspectul…
        </p>
      </>
    );
  }
  const dto = q.data;
  const d = dto.draft;
  const T = THEMES[d.theme];
  const n = dto.changes.length;
  const setDto = (next: AppearanceDto) => qc.setQueryData(keys.appearance(rid), next);

  /** Saves the whole draft; `undo` restores the previous draft from the toast (prototype "Anulează"). */
  async function save(next: Appearance, message?: string, undo?: Appearance) {
    if (busy || !canEdit) return;
    setBusy("save");
    try {
      setDto(await ap.save(next));
      if (message) {
        toast(message, undo ? async () => setDto(await ap.save(undo)) : undefined);
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
      await qc.invalidateQueries({ queryKey: keys.appearance(rid) });
    } finally {
      setBusy(null);
    }
  }

  async function onImage(kind: BrandImageKind, e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || busy || !canEdit) return;
    const problem = brandFileError(file, kind);
    setImgErr((x) => ({ ...x, [kind]: problem ?? "" }));
    if (problem) return;
    setBusy(kind);
    try {
      const media = await ap.upload(kind, await prepareBrandImage(file, kind));
      const latest = qc.getQueryData<AppearanceDto>(keys.appearance(rid))?.draft ?? d;
      setDto(
        await ap.save(kind === "logo" ? { ...latest, logoId: media.id } : { ...latest, coverId: media.id }),
      );
    } catch (err) {
      setImgErr((x) => ({ ...x, [kind]: err instanceof Error ? err.message : String(err) }));
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    try {
      setDto(await ap.publish(dto.revision));
      setDialog(null);
      toast("Aspectul a fost publicat.");
    } catch (err) {
      setDialog(null);
      await qc.invalidateQueries({ queryKey: keys.appearance(rid) });
      toast(err instanceof Error ? err.message : String(err));
    }
  }

  async function discard() {
    const prev = d;
    try {
      setDto(await ap.discard(dto.revision));
      setDialog(null);
      toast("Aspectul a revenit la versiunea publicată.", async () => setDto(await ap.save(prev)));
    } catch (err) {
      setDialog(null);
      await qc.invalidateQueries({ queryKey: keys.appearance(rid) });
      toast(err instanceof Error ? err.message : String(err));
    }
  }

  const logoBg = d.logoId ? `url("${mediaUrl(rid, d.logoId)}") center / cover no-repeat, #FFFFFF` : "#3B2A22";
  const initials = initialsOf(membership.restaurant.name);

  return (
    <>
      {header}
      <Bar
        sticky
        tone={n ? "warning" : "success"}
        icon={n ? TriangleAlert : CircleCheck}
        title={
          n
            ? plural(n, "modificare de aspect nepublicată", "modificări de aspect nepublicate")
            : "Aspectul publicat este la zi"
        }
        sub={n ? "Clienții văd aspectul publicat." : `Tema ${T.label}.`}
      >
        {n > 0 && canEdit && (
          <button type="button" className="btn-link" onClick={() => setDialog("discard")}>
            Renunță la modificări
          </button>
        )}
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => openPreview(rid, main?.id)}>
          <Eye {...ICON} aria-hidden="true" />
          Previzualizează
        </button>
        {canEdit && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!n}
            onClick={() => n && setDialog("publish")}
          >
            Publică aspectul
          </button>
        )}
      </Bar>
      {!canEdit && <Alert tone="info">Doar proprietarul sau managerul poate modifica aspectul.</Alert>}

      <div className="ap-grid">
        <div className="stack" style={{ gap: 20, minWidth: 0 }} aria-busy={busy ? true : undefined}>
          <section className="card ap-card" aria-labelledby="ap-th">
            <h2 id="ap-th" className="h2">
              Temă
            </h2>
            <div role="radiogroup" aria-labelledby="ap-th" className="ap-themes">
              {THEME_IDS.map((k) => {
                const t = THEMES[k];
                const on = k === d.theme;
                return (
                  <label key={k} className={`ap-theme${on ? " on" : ""}${canEdit ? "" : " ro"}`}>
                    <input
                      type="radio"
                      name="ap-theme"
                      className="sr-only"
                      checked={on}
                      disabled={!canEdit}
                      onChange={() => save(withTheme(d, k))}
                    />
                    <span
                      aria-hidden="true"
                      className="ap-swatch"
                      style={{ background: t.bg, borderColor: t.line }}
                    >
                      <span
                        style={{
                          fontFamily: t.fd,
                          fontSize: 18,
                          fontWeight: t.kicker ? 600 : undefined,
                          color: t.ink,
                        }}
                      >
                        {membership.restaurant.name}
                      </span>
                      <span style={{ display: "flex", gap: 4 }}>
                        <span
                          style={{
                            height: 14,
                            width: 40,
                            borderRadius: t.chipR,
                            background: on ? d.accent : ACCENTS[k][0]?.[0],
                          }}
                        />
                        <span
                          style={{
                            height: 14,
                            width: 28,
                            borderRadius: t.chipR,
                            border: `1px solid ${t.line}`,
                          }}
                        />
                      </span>
                    </span>
                    <span className="ap-theme-name">
                      {on && <Check {...ICON} aria-hidden="true" />}
                      {t.label}
                    </span>
                    <span className="ap-theme-desc">{THEME_DESC[k]}</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="card ap-card" aria-labelledby="ap-ac">
            <div className="stack" style={{ gap: 2 }}>
              <h2 id="ap-ac" className="h2">
                Culoare de accent
              </h2>
              <span className="ap-note">
                Folosită pentru categoria activă și program. Doar culori verificate pentru contrast cu tema
                aleasă.
              </span>
            </div>
            <div role="radiogroup" aria-labelledby="ap-ac" className="ap-accents">
              {ACCENTS[d.theme].map(([hex, label]) => {
                const on = hex === d.accent;
                return (
                  <label key={hex} className={`ap-accent${canEdit ? "" : " ro"}`}>
                    <input
                      type="radio"
                      name="ap-accent"
                      className="sr-only"
                      checked={on}
                      disabled={!canEdit}
                      onChange={() => save({ ...d, accent: hex })}
                    />
                    <span
                      aria-hidden="true"
                      className="ap-dot"
                      style={{
                        background: hex,
                        boxShadow: on
                          ? "0 0 0 2px #FFFFFF, 0 0 0 4px #2A1B4D"
                          : "inset 0 0 0 1px rgba(0,0,0,0.1)",
                      }}
                    />
                    <span className="ap-accent-text">
                      <span className="ap-accent-name">{label}</span>
                      <span className="ap-note">{contrastText(d.theme, hex)}</span>
                    </span>
                    <span className="ap-check">{on && <Check {...ICON} aria-hidden="true" />}</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="card ap-card" aria-labelledby="ap-lg" style={{ gap: 16 }}>
            <h2 id="ap-lg" className="h2">
              Logo și copertă
            </h2>
            <div className="ap-logo-row">
              <div
                aria-hidden="true"
                className="ap-logo"
                style={{ borderRadius: logoRadius(d.theme), background: logoBg }}
              >
                {d.logoId ? "" : initials}
              </div>
              <div className="stack" style={{ flex: "1 1 200px", gap: 6 }}>
                <span className="ap-label">Logo</span>
                <input
                  ref={logoInput}
                  type="file"
                  accept={BRAND_ACCEPT.logo}
                  onChange={(e) => onImage("logo", e)}
                  tabIndex={-1}
                  aria-hidden="true"
                  hidden
                />
                {canEdit && (
                  <div className="row" style={{ gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={busy === "logo"}
                      onClick={() => logoInput.current?.click()}
                    >
                      {busy === "logo" ? "Se încarcă…" : d.logoId ? "Înlocuiește logo-ul" : "Încarcă logo"}
                    </button>
                    {d.logoId && (
                      <button type="button" className="btn-link" onClick={() => save({ ...d, logoId: null })}>
                        Folosește inițialele
                      </button>
                    )}
                  </div>
                )}
                <span role="alert" className="ap-err">
                  {imgErr.logo}
                </span>
                <span className="ap-note">
                  {T.initials
                    ? "PNG, JPG, WebP sau SVG, până la 4 MB, pătrat. Fără logo, folosim inițialele."
                    : `PNG, JPG, WebP sau SVG, până la 4 MB, pătrat. Fără logo, tema ${T.label} afișează doar numele.`}
                </span>
              </div>
            </div>
            <div className="stack ap-cover-block">
              <span className="ap-label">Fotografie de copertă</span>
              <div
                aria-hidden="true"
                className={`ap-cover${d.coverId ? "" : " empty"}`}
                style={
                  d.coverId
                    ? { background: `url("${mediaUrl(rid, d.coverId)}") center / cover no-repeat` }
                    : undefined
                }
              >
                {d.coverId ? "" : "Fără copertă"}
              </div>
              <input
                ref={coverInput}
                type="file"
                accept={BRAND_ACCEPT.cover}
                onChange={(e) => onImage("cover", e)}
                tabIndex={-1}
                aria-hidden="true"
                hidden
              />
              {canEdit && (
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={busy === "cover"}
                    onClick={() => coverInput.current?.click()}
                  >
                    {busy === "cover" ? "Se încarcă…" : d.coverId ? "Înlocuiește coperta" : "Încarcă copertă"}
                  </button>
                  {d.coverId && (
                    <button type="button" className="btn-link" onClick={() => save({ ...d, coverId: null })}>
                      Elimină coperta
                    </button>
                  )}
                </div>
              )}
              <span role="alert" className="ap-err">
                {imgErr.cover}
              </span>
              <span className="ap-note" style={{ color: T.cover ? undefined : "var(--color-warning)" }}>
                {T.cover
                  ? "Recomandat 1600 × 600 px. JPG, PNG sau WebP, până la 8 MB."
                  : `Tema ${T.label} nu afișează copertă. Imaginea se păstrează dacă revii la altă temă.`}
              </span>
            </div>
          </section>
          {canEdit && (
            <button
              type="button"
              className="ap-reset"
              onClick={() =>
                save(
                  themeDefaults(d),
                  "Accentul și coperta au revenit la valorile implicite ale temei, în ciornă.",
                  d,
                )
              }
            >
              Revino la setările implicite ale temei
            </button>
          )}
        </div>
        <MiniPreview
          rid={rid}
          draft={d}
          menuId={main?.id ?? null}
          fallbackName={membership.restaurant.name}
        />
      </div>

      {dialog === "publish" && (
        <Modal
          title="Publici aspectul nou?"
          body="Clienții vor vedea noul aspect imediat. Conținutul meniului nu se schimbă. Linkul și codul QR rămân aceleași."
          onClose={() => setDialog(null)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setDialog(null)}>
                Renunță
              </button>
              <button type="button" className="btn btn-primary" data-autofocus="" onClick={publish}>
                Publică aspectul
              </button>
            </>
          }
        >
          <ChangeList dto={dto} />
        </Modal>
      )}
      {dialog === "discard" && (
        <Modal
          alert
          title="Renunți la modificările de aspect?"
          body="Ciorna revine la aspectul publicat. Poți anula imediat după."
          onClose={() => setDialog(null)}
          actions={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                data-autofocus=""
                onClick={() => setDialog(null)}
              >
                Păstrează ciorna
              </button>
              <button type="button" className="btn btn-danger" onClick={discard}>
                Renunță la modificări
              </button>
            </>
          }
        >
          <ChangeList dto={dto} />
        </Modal>
      )}
    </>
  );
}

function ChangeList({ dto }: { dto: AppearanceDto }) {
  if (!dto.changes.length) return null;
  return (
    <ul className="diff-list">
      {dto.changes.map((c) => (
        <li key={c.field}>
          <span style={{ color: "var(--color-warning)", display: "flex", paddingTop: 2 }}>
            <Pencil size={14} strokeWidth={1.75} aria-hidden="true" />
          </span>
          <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <span className="sub">{c.label}</span>
            <span className="chg">{c.change}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * "Previzualizare live · ciornă" (prototype mini phone): the draft theme and accent over the restaurant's own
 * draft name, description, first categories and products. Decorative; the full draft opens with "Previzualizează".
 */
function MiniPreview({
  rid,
  draft,
  menuId,
  fallbackName,
}: {
  rid: string;
  draft: Appearance;
  menuId: string | null;
  fallbackName: string;
}) {
  const profile = useProfile(rid);
  const menu = useDraft(rid, menuId);
  const T = THEMES[draft.theme];
  // Same rule as the public menu: a "fill" accent (Brunch) only sits behind ink text.
  const mt = { ...T, chipBg: draft.accent, accent: T.accentUse === "fill" ? T.ink : draft.accent };
  const hasCover = T.cover && !!draft.coverId;
  const name = profile.data?.draft.name || fallbackName;
  const tagline = profile.data?.draft.descRo ?? "";
  const city = T.kicker ? (profile.data?.draft.city ?? "") : "";
  const cats = (menu.data?.categories ?? []).slice(0, T.nav === "rail" ? 5 : 3);
  const items = (menu.data?.products ?? []).slice(0, T.layout === "tiles" ? 4 : 3);
  const currency = menu.data?.currency ?? "RON";
  const showLogo = !!draft.logoId || T.initials;
  const priceText = (p: (typeof items)[number]) =>
    p.variants?.length
      ? p.variants.map((v) => `${v.label} ${price(v.priceMinor, currency)}`).join(" · ")
      : price(p.priceMinor, currency);
  const photo = (p: (typeof items)[number], className: string, radius: string) =>
    p.imageKey &&
    T.listPhotos && (
      <div
        className={className}
        style={{
          borderRadius: radius,
          background: `url("${mediaUrl(rid, p.imageKey)}") center / cover no-repeat, ${mt.line}`,
        }}
      />
    );

  const head = (
    <div
      className="ap-mini-head"
      style={{
        padding: hasCover && T.nav !== "dock" ? "10px 14px 12px" : "18px 14px 12px",
        ...(T.nav === "dock" && {
          justifyContent: "flex-end",
          minHeight: 150,
          background: hasCover
            ? `linear-gradient(rgba(18,14,12,.3), ${mt.bg}), url("${mediaUrl(rid, draft.coverId ?? "")}") center / cover no-repeat`
            : `radial-gradient(120% 90% at 85% 0%, ${draft.accent}29, transparent 60%), ${mt.surface}`,
        }),
      }}
    >
      {showLogo && (
        <div
          className="ap-mini-logo"
          style={{
            borderRadius: logoRadius(draft.theme),
            background: draft.logoId
              ? `url("${mediaUrl(rid, draft.logoId)}") center / cover no-repeat, #FFFFFF`
              : T.nav === "rail"
                ? mt.chipBg
                : T.ink,
            color: T.nav === "rail" ? mt.chipFg : mt.bg,
            border: `3px solid ${mt.bg}`,
            fontFamily: mt.fd,
          }}
        >
          {draft.logoId ? "" : initialsOf(name)}
        </div>
      )}
      {city && (
        <span
          className="ap-mini-over"
          style={{
            fontFamily: mt.fm,
            color: T.nav === "tabs" || T.nav === "dock" ? mt.accent : mt.muted,
          }}
        >
          {city}
        </span>
      )}
      <span
        style={{
          fontFamily: mt.fd,
          fontSize: T.nav === "strip" || T.nav === "dock" ? 34 : 26,
          fontWeight: T.kicker ? 700 : undefined,
          letterSpacing: T.kicker ? "-0.03em" : undefined,
          lineHeight: 1.1,
        }}
      >
        {name}
      </span>
      {tagline && <span style={{ fontSize: 13, color: mt.muted }}>{tagline}</span>}
    </div>
  );

  const nav =
    cats.length === 0 ? null : T.nav === "tabs" ? (
      <div className="ap-mini-tabs" style={{ borderColor: mt.line }}>
        {cats.map((c, i) => (
          <span
            key={c.id}
            style={{ color: i === 0 ? mt.ink : mt.muted, borderColor: i === 0 ? mt.chipBg : "transparent" }}
          >
            {c.name}
          </span>
        ))}
      </div>
    ) : T.nav === "strip" ? (
      <div className="ap-mini-strip" style={{ borderColor: mt.ink }}>
        {cats.map((c, i) => (
          <span
            key={c.id}
            style={{ borderColor: mt.ink, ...(i === 0 && { background: mt.chipBg, color: mt.chipFg }) }}
          >
            {c.name}
          </span>
        ))}
      </div>
    ) : T.nav === "rail" ? (
      <div className="ap-mini-rail" style={{ borderColor: mt.line }}>
        {cats.map((c, i) => (
          <span
            key={c.id}
            style={{ color: i === 0 ? mt.ink : mt.muted, borderColor: i === 0 ? mt.chipBg : "transparent" }}
          >
            <span style={{ fontFamily: mt.fm, fontSize: 15, fontWeight: 300 }}>
              {String(i + 1).padStart(2, "0")}
            </span>
            {c.name}
          </span>
        ))}
      </div>
    ) : T.nav === "dock" ? (
      <div className="ap-mini-dock" style={{ background: mt.surface, borderColor: mt.line }}>
        {cats.map((c, i) => (
          <span
            key={c.id}
            style={{
              borderRadius: mt.chipR,
              color: i === 0 ? mt.chipFg : mt.muted,
              background: i === 0 ? mt.chipBg : undefined,
            }}
          >
            {c.name}
          </span>
        ))}
      </div>
    ) : (
      <div className="ap-mini-chips" style={{ borderColor: mt.line }}>
        {cats.map((c, i) => (
          <span
            key={c.id}
            style={{
              borderRadius: mt.chipR,
              ...(i === 0
                ? { background: mt.chipBg, color: mt.chipFg }
                : { border: `1px solid ${mt.line}`, color: T.kicker ? mt.muted : undefined }),
            }}
          >
            {c.name}
          </span>
        ))}
      </div>
    );

  const list =
    T.layout === "tiles" ? (
      <div className="ap-mini-tiles">
        {items.map((p) => (
          <div key={p.id} className="ap-mini-tile" style={{ background: mt.surface, borderRadius: 14 }}>
            {photo(p, "ap-mini-tile-photo", "0")}
            <div className="stack" style={{ gap: 2, padding: 8 }}>
              <span style={{ fontSize: 13, fontWeight: mt.nameW }}>{p.name}</span>
              <span style={{ fontSize: 12, fontWeight: mt.pw }}>{priceText(p)}</span>
            </div>
          </div>
        ))}
      </div>
    ) : (
      items.map((p, i) => (
        <div
          key={p.id}
          className={`ap-mini-item${T.layout === "plates" ? " plate" : ""}`}
          style={{
            borderColor: mt.line,
            ...(T.layout === "plates" && { background: mt.surface, borderRadius: 14 }),
          }}
        >
          {T.layout === "accordion" && (
            <span style={{ fontFamily: mt.fm, fontSize: 11, color: mt.muted, paddingTop: 3 }}>
              {String(i + 1).padStart(2, "0")}
            </span>
          )}
          {T.layout === "plates" && photo(p, "ap-mini-photo", mt.imgR)}
          <div className="stack" style={{ flex: 1, gap: 2, minWidth: 0 }}>
            <span style={{ fontFamily: mt.fd, fontSize: 16, fontWeight: T.kicker ? mt.nameW : undefined }}>
              {p.name}
            </span>
            {T.layout === "lines" || T.layout === "plates" ? (
              p.portion && (
                <span
                  style={{
                    fontFamily: mt.fm,
                    fontSize: 11,
                    color: T.layout === "plates" ? mt.accent : mt.muted,
                  }}
                >
                  {p.portion}
                </span>
              )
            ) : (
              <span style={{ fontFamily: mt.pf, fontStyle: mt.ps, fontWeight: mt.pw, fontSize: 14 }}>
                {priceText(p)}
              </span>
            )}
          </div>
          {(T.layout === "lines" || T.layout === "plates") && (
            <span style={{ fontWeight: mt.pw, fontSize: 14, whiteSpace: "nowrap" }}>{priceText(p)}</span>
          )}
          {T.layout === "accordion" && (
            <span className="ap-mini-plus" style={{ borderColor: mt.ink }}>
              +
            </span>
          )}
          {(T.layout === "rows" || T.layout === "cards") && photo(p, "ap-mini-photo", mt.imgR)}
        </div>
      ))
    );

  return (
    <section aria-label="Previzualizare live a ciornei" className="ap-mini-wrap">
      <span className="ap-mini-title">Previzualizare live · ciornă</span>
      <div
        aria-hidden="true"
        className={`ap-mini nav-${T.nav}`}
        style={{ borderColor: mt.line, background: mt.bg, color: mt.ink, fontFamily: mt.fb }}
      >
        {hasCover && T.nav !== "dock" && (
          <div
            className="ap-mini-cover"
            style={{ background: `url("${mediaUrl(rid, draft.coverId ?? "")}") center / cover no-repeat` }}
          />
        )}
        {head}
        {T.nav === "rail" ? (
          <div className="ap-mini-split">
            {nav}
            <div className="ap-mini-col">{list}</div>
          </div>
        ) : T.nav === "dock" ? (
          <>
            {list}
            {nav}
          </>
        ) : (
          <>
            {nav}
            {list}
          </>
        )}
      </div>
    </section>
  );
}
